import { memo, useEffect } from 'react'

import { CorporateFare } from '@mui/icons-material'
import { ListItemText, styled, SvgIcon } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useMatchRoute } from '@tanstack/react-router'
import { Cubes } from 'emotion-icons/fa-solid'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import SimpleBar from 'simplebar-react'

import { Collapse, collapseAnimationFactory } from '@shared/components/Collapse'
import { LoadingIndicator } from '@shared/layouts'
import { ListItemButtonLink } from 'components/LinkedMUIComponents'
import { NavLinkListItem, NavLinkStartIcon, NavLinkListItemTextPendable } from 'domains/_Sidebar/ListItemNavLink'
import { SectionDivider } from 'domains/_Sidebar/SectionDivider'
import { Navigation as ContentSystemNavigation } from 'domains/_Sidebar/Sections/ContentSystem'
import { Navigation as ContentWorkspacesNavigation } from 'domains/_Sidebar/Sections/ContentWorkspaces'
import { useRecentWorkspaces } from 'domains/_Sidebar/Sections/ContentWorkspaces/Workspace'
import { Navigation as ExpApplicationsNavigation } from 'domains/_Sidebar/Sections/ExpApplications'
import { Navigation as MediaOTTNavigation } from 'domains/_Sidebar/Sections/MediaOTT'
import { useRecentPlatforms } from 'domains/_Sidebar/Sections/MediaOTT/Platforms'
import { NavigationList } from 'layouts'
import { manageAccessRoute } from 'pages/NWPlatform/Environment'
import { mediaPlatformRoute } from 'pages/NWPlatform/MediaPlatforms/MediaPlatform'
import { environmentsOverviewRoute } from 'pages/NWPlatform/Organizations/Organization/Environments'
import { membersRoute, usersOverviewRoute } from 'pages/NWPlatform/Organizations/Organization/Members'
import { teamsRoute } from 'pages/NWPlatform/Organizations/Organization/Teams'
import { workspaceRoute } from 'pages/NWPlatform/Workspaces/Workspace'
import { organizationQueryKeys } from 'services/queryKeys'

const ScrollContainer = styled(SimpleBar)(({ theme }) => ({
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: 'auto',
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  paddingBottom: theme.spacing(2),
}))

const EnvironmentCollapse = styled(Collapse)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  overflow: 'hidden',
})

type NavigationProps = {
  organizationId: string
  environmentId?: string
}

const NavigationRaw = ({ organizationId, environmentId }: NavigationProps) => {
  const { t } = useTranslation(['common', 'entity'])
  const matchRoute = useMatchRoute()

  const [recentWorkspaces, setRecentWorkspaces] = useRecentWorkspaces()
  const [recentPlatforms, setRecentPlatforms] = useRecentPlatforms()

  const workspaceRouteMatch = matchRoute({ to: workspaceRoute.to, fuzzy: true })
  const platformRouteMatch = matchRoute({ to: mediaPlatformRoute.to, fuzzy: true })

  const organizationQuery = useQuery({
    ...organizationQueryKeys.detail({ organizationId }),
  })

  useEffect(() => {
    if (environmentId) {
      if (workspaceRouteMatch && !recentWorkspaces[environmentId]?.includes(workspaceRouteMatch.workspaceId)) {
        setRecentWorkspaces({ environmentId, workspaceId: workspaceRouteMatch.workspaceId })
      }
    }
  }, [environmentId, recentWorkspaces, setRecentWorkspaces, workspaceRouteMatch])

  useEffect(() => {
    if (environmentId) {
      if (platformRouteMatch && !recentPlatforms[environmentId]?.includes(platformRouteMatch.platformId)) {
        setRecentPlatforms({ environmentId, platformId: platformRouteMatch.platformId })
      }
    }
  }, [environmentId, recentPlatforms, setRecentPlatforms, platformRouteMatch])

  if (organizationQuery.isLoading) {
    return <LoadingIndicator sx={{ mt: 1, mb: 3, mx: 3 }} />
  }

  return (
    <ScrollContainer>
      <NavigationList sx={{ ml: 2, pr: 2 }} component={motion.ul}>
        <NavLinkListItem disablePadding component={motion.li} whileHover={{ translateX: 2 }}>
          <ListItemButtonLink
            to={usersOverviewRoute.to}
            params={{ organizationId }}
            selected={
              !!(
                matchRoute({ to: membersRoute.to, fuzzy: true }) ||
                matchRoute({ to: teamsRoute.to, fuzzy: true }) ||
                matchRoute({ to: environmentsOverviewRoute.to })
              )
            }
          >
            <NavLinkStartIcon>
              <CorporateFare />
            </NavLinkStartIcon>
            <ListItemText
              disableTypography
              primary={
                <NavLinkListItemTextPendable>{t('entity:org.organization', { count: 1 })}</NavLinkListItemTextPendable>
              }
            />
          </ListItemButtonLink>
        </NavLinkListItem>

        <EnvironmentCollapse collapsed={!environmentId} component="li" key={environmentId}>
          <NavigationList component={motion.ul} variants={collapseAnimationFactory({ opacity: 0 }, { opacity: 1 })}>
            <NavLinkListItem disablePadding component={motion.li} whileHover={{ translateX: 2 }}>
              <ListItemButtonLink
                to={manageAccessRoute.to}
                params={{ organizationId, environmentId: environmentId! }}
                selected={!!matchRoute({ to: manageAccessRoute.to, fuzzy: true })}
              >
                <NavLinkStartIcon>
                  <SvgIcon>
                    <Cubes />
                  </SvgIcon>
                </NavLinkStartIcon>
                <ListItemText
                  disableTypography
                  primary={
                    <NavLinkListItemTextPendable>
                      {t('entity:org.environment', { count: 1 })}
                    </NavLinkListItemTextPendable>
                  }
                />
              </ListItemButtonLink>
            </NavLinkListItem>

            <SectionDivider />

            <ContentWorkspacesNavigation organizationId={organizationId} environmentId={environmentId!} />

            <MediaOTTNavigation organizationId={organizationId} environmentId={environmentId!} />

            <ExpApplicationsNavigation organizationId={organizationId} environmentId={environmentId!} />

            <ContentSystemNavigation organizationId={organizationId} environmentId={environmentId!} />
          </NavigationList>
        </EnvironmentCollapse>
      </NavigationList>
    </ScrollContainer>
  )
}

export const Navigation = memo(NavigationRaw)
